/**
 * FactFindFormSkeleton — loading placeholder for the fact-find form while the
 * client graph values load. Mirrors ClientGraphTree's two-column layout (sections
 * sidebar + field cards) so the transition to real content is seamless.
 */
import React from 'react';
import { Box, Skeleton } from '@mui/material';

const SIDEBAR_ROWS = 8;
const CARDS = 3;
const FIELDS_PER_CARD = 6;

const FactFindFormSkeleton: React.FC = () => (
  <Box
    aria-busy="true"
    aria-label="Loading fact find"
    sx={{
      display: 'flex',
      height: 'calc(100vh - 180px)',
      overflow: 'hidden',
      bgcolor: '#f0f2f5',
      borderRadius: 2,
    }}
  >
    {/* Sidebar */}
    <Box
      sx={{
        width: 220,
        minWidth: 220,
        bgcolor: '#fff',
        borderRight: '1px solid #dde1e7',
        flexShrink: 0,
        p: 2,
      }}
    >
      <Skeleton variant="text" width="45%" height={16} sx={{ mb: 2 }} />
      {Array.from({ length: SIDEBAR_ROWS }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 1.1 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={`${55 + ((i * 7) % 35)}%`} height={16} />
        </Box>
      ))}
    </Box>

    {/* Main content — section cards with field placeholders */}
    <Box sx={{ flex: 1, overflowY: 'hidden', p: 3 }}>
      {Array.from({ length: CARDS }).map((_, c) => (
        <Box
          key={c}
          sx={{
            bgcolor: '#fff',
            border: '1px solid #e0e3e7',
            borderRadius: 2,
            p: 2.5,
            mb: 2,
          }}
        >
          <Skeleton variant="text" width="32%" height={26} sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 2,
            }}
          >
            {Array.from({ length: FIELDS_PER_CARD }).map((__, f) => (
              <Skeleton
                key={f}
                variant="rectangular"
                height={48}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export default FactFindFormSkeleton;
